import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useAuth } from '../../contexts/AuthContext';
import documentStorage from '../../services/documentStorage';
import { runRAGQuery } from '../../rag/ragEngine';
import { supabase } from '../../lib/supabase';
import { logEvent } from '../../lib/activityLogger';
import { rateAnswer } from '../../services/ragReviewService';
import { useHOA } from '../../contexts/HOAContext';
import { createId } from '../../utils/createId';
import {
  openDocumentUrlNatively,
  reserveNativeDocumentWindow,
  shouldUseNativeDocumentOpen,
  showReservedDocumentWindowError,
} from '../../utils/mobileDocumentOpen';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Example queries shown when the AI Search bar is focused with no active conversation.
// Confirmed accurate by Philippe against the live document set (2026-08-10).
const EXAMPLE_QUERIES = [
  { icon: 'directions_boat', text: 'Can I park my boat in my driveway?' },
  { icon: 'payments', text: 'How much are my annual dues and when are they due?' },
  { icon: 'palette', text: 'Do I need approval before painting my house a new color?' },
];

const FOLDERS = [
  {
    key: 'Governing',
    label: 'Governing Documents',
    icon: 'gavel',
    categories: ['ccr', 'amendment', 'bylaw', 'resolution'],
  },
  {
    key: 'Legal',
    label: 'Legal & Contracts',
    icon: 'description',
    categories: ['legal', 'contract', 'insurance', 'notice'],
  },
  {
    key: 'Financial',
    label: 'Financial Reports',
    icon: 'trending_up',
    categories: ['financial'],
  },
  {
    key: 'Minutes',
    label: 'Meeting Minutes',
    icon: 'event_note',
    categories: ['minutes'],
  },
];

const folderIcon = (folder) => {
  if (folder.system_key === 'governing') return 'gavel';
  if (folder.system_key === 'financial') return 'trending_up';
  if (folder.system_key === 'meeting-minutes') return 'event_note';
  if (folder.system_key === 'legal-contracts') return 'description';
  if (folder.system_key === 'plat-maps-parcels') return 'map';
  if (folder.system_key === 'acc-documents') return 'fact_check';
  return 'folder';
};

const folderFromDb = (folder) => ({
  key: folder.id || folder.system_key || folder.name,
  id: folder.id,
  label: folder.name,
  icon: folderIcon(folder),
  system_key: folder.system_key,
  categories: folder.category_filter || [],
});

const sortLibraryDocuments = (docs = []) => {
  return [...docs].sort((a, b) => {
    const aManual = a.display_order !== null && a.display_order !== undefined;
    const bManual = b.display_order !== null && b.display_order !== undefined;
    if (aManual && bManual) return a.display_order - b.display_order;
    if (aManual !== bManual) return aManual ? 1 : -1;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
};

const categoryLabel = (cat) => ({
  ccr: 'CC&R', amendment: 'Amendment', bylaw: 'Bylaw',
  resolution: 'Resolution', minutes: 'Minutes', financial: 'Financial',
  legal: 'Legal', acc: 'ACC', notice: 'Notice', contract: 'Contract',
  insurance: 'Insurance', other: 'Other',
}[cat] || cat);

const formatDate = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getDocumentExtension = (doc) => {
  const source = [doc?.file_name, doc?.title, doc?.file_url]
    .filter(Boolean)
    .join(' ')
    .split('?')[0]
    .toLowerCase();
  const match = source.match(/\.([a-z0-9]+)(?:\s|$)/i);
  return match?.[1] || '';
};

const getPreviewKind = (doc) => {
  const mime = (doc?.mime_type || '').toLowerCase();
  const ext = getDocumentExtension(doc);

  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  if (mime.includes('wordprocessingml') || ext === 'docx') return 'word';
  if (mime.includes('spreadsheetml') || ['xlsx', 'xls', 'csv'].includes(ext)) return 'spreadsheet';
  if (mime.startsWith('text/') || ['txt', 'md', 'rtf', 'json'].includes(ext)) return 'text';
  if (mime.includes('presentationml') || ['pptx', 'ppt'].includes(ext)) return 'browser';
  return 'browser';
};

const previewLabel = (doc) => {
  const mime = doc?.mime_type || '';
  const ext = getDocumentExtension(doc);
  if (mime) return mime;
  if (ext) return `.${ext}`;
  return 'Document';
};

const documentIcon = (doc) => {
  const kind = getPreviewKind(doc);
  if (kind === 'image') return 'image';
  if (kind === 'word') return 'article';
  if (kind === 'spreadsheet') return 'table_chart';
  if (kind === 'text') return 'notes';
  if (kind === 'pdf') return 'picture_as_pdf';
  return 'draft';
};

// FIX 2: font-medium throughout, strips ** and *italic* markdown
const renderAnswer = (text) => {
  if (!text) return null;

  const lines = text.split(/\n+/).map(l => l.trim()).filter(Boolean);
  const elements = [];
  let bulletGroup = [];

  const flushBullets = (key) => {
    if (bulletGroup.length === 0) return;
    elements.push(
      <ul key={`bullets-${key}`} className="space-y-1.5 my-2">
        {bulletGroup.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0 mt-[0.45rem]" />
            <span className="text-sm font-medium text-primary-dark leading-relaxed">
              {item}
            </span>
          </li>
        ))}
      </ul>
    );
    bulletGroup = [];
  };

  lines.forEach((line, i) => {
    const stripped = line
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .trim();

    const isSourceLine = /^sources?:/i.test(stripped) ||
      (i === lines.length - 1 && /section|cc&r|bylaw|article/i.test(stripped));

    if (isSourceLine) {
      flushBullets(i);
      const sourceText = stripped.replace(/^sources?:\s*/i, '');
      elements.push(
        <p key={i} className="text-xs italic font-medium text-slate-400 mt-4 leading-relaxed border-t border-amber-900/10 pt-3">
          {sourceText}
        </p>
      );
      return;
    }

    const isHeader = /^(phase\s*\d|both phases?|section\s*\d)/i.test(stripped) ||
      (stripped.endsWith(':') && stripped.length < 40);

    if (isHeader) {
      flushBullets(i);
      const headerText = stripped.replace(/:$/, '');
      elements.push(
        <p key={i} className="text-[11px] font-bold uppercase tracking-widest text-secondary/70 mt-4 mb-1">
          {headerText}
        </p>
      );
      return;
    }

    const isBullet = /^[-*•]\s+/.test(line);
    const bulletText = stripped.replace(/^[-*•]\s+/, '');

    if (isBullet && bulletText.length < 120) {
      bulletGroup.push(bulletText);
      return;
    }

    flushBullets(i);
    if (isBullet) {
      elements.push(
        <p key={i} className="text-sm font-medium text-primary-dark leading-relaxed mt-2">
          {bulletText}
        </p>
      );
    } else {
      elements.push(
        <p key={i} className="text-sm font-medium text-primary-dark leading-relaxed mt-2 first:mt-0">
          {stripped}
        </p>
      );
    }
  });

  flushBullets('end');
  return elements;
};

const MemberDocumentViewer = ({ doc, onClose }) => {
  const previewKind = getPreviewKind(doc);
  const isPdfPreview = previewKind === 'pdf';
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [viewerError, setViewerError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [wordHtml, setWordHtml] = useState('');
  const [spreadsheetSheets, setSpreadsheetSheets] = useState([]);
  const [textPreview, setTextPreview] = useState('');
  const viewportRef = useRef(null);
  const pageRefs = useRef({});
  const [availableWidth, setAvailableWidth] = useState(720);

  useEffect(() => {
    if (!viewportRef.current) return;
    const updateWidth = () => {
      const width = viewportRef.current?.clientWidth || 720;
      setAvailableWidth(width);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const pages = Object.entries(pageRefs.current)
      .map(([pageNumber, element]) => ({ pageNumber: Number(pageNumber), element }))
      .filter(({ element }) => element);

    if (!isPdfPreview || pages.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.dataset?.pageNumber) {
          setCurrentPage(Number(visible.target.dataset.pageNumber));
        }
      },
      { root: viewportRef.current, threshold: [0.35, 0.6] }
    );

    pages.forEach(({ element }) => observer.observe(element));
    return () => observer.disconnect();
  }, [numPages, sidebarOpen, isPdfPreview]);

  useEffect(() => {
    let mounted = true;
    setViewerError('');
    setWordHtml('');
    setSpreadsheetSheets([]);
    setTextPreview('');

    if (!doc.content || isPdfPreview || previewKind === 'image' || previewKind === 'browser') return;

    const loadPreview = async () => {
      setPreviewLoading(true);
      try {
        const response = await fetch(doc.content);
        if (!response.ok) throw new Error(`File request failed with ${response.status}.`);

        if (previewKind === 'word') {
          const mammoth = await import('mammoth');
          const arrayBuffer = await response.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          if (!mounted) return;
          setWordHtml(result.value || '<p>No readable text was found in this Word document.</p>');
        }

        if (previewKind === 'spreadsheet') {
          const XLSX = await import('xlsx');
          const arrayBuffer = await response.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const sheets = workbook.SheetNames.map((sheetName) => {
            const sheet = workbook.Sheets[sheetName];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false }).slice(0, 200);
            return { name: sheetName, rows };
          });
          if (!mounted) return;
          setSpreadsheetSheets(sheets);
        }

        if (previewKind === 'text') {
          const text = await response.text();
          if (!mounted) return;
          setTextPreview(text);
        }
      } catch (error) {
        if (!mounted) return;
        setViewerError(error?.message || 'This document could not be loaded in the portal viewer.');
      } finally {
        if (mounted) setPreviewLoading(false);
      }
    };

    loadPreview();
    return () => {
      mounted = false;
    };
  }, [doc.content, isPdfPreview, previewKind]);

  const pageWidth = Math.round(Math.min(Math.max(availableWidth - 64, 360), 960) * scale);

  const scrollToPage = (pageNumber) => {
    pageRefs.current[pageNumber]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setCurrentPage(pageNumber);
  };

  const handleDownload = () => {
    if (!doc.content) return;
    const link = document.createElement('a');
    link.href = doc.content;
    link.download = doc.title || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInNewTab = () => {
    if (doc.content) window.open(doc.content, '_blank', 'noopener,noreferrer');
  };

  const showPreviousPage = () => scrollToPage(Math.max(currentPage - 1, 1));
  const showNextPage = () => scrollToPage(Math.min(currentPage + 1, numPages || currentPage));

  const renderUnsupportedState = (message = 'Open or download the document to view it.') => (
    <div className="h-full flex flex-col items-center justify-center text-center py-24">
      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 warm-tactile-shadow">
        <span className="material-symbols-outlined text-3xl text-secondary">draft</span>
      </div>
      <p className="text-base font-bold text-primary-dark mb-2">Preview unavailable</p>
      <p className="text-sm font-medium text-slate-500 mb-6 max-w-md leading-relaxed">{message}</p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={openInNewTab}
          className="border border-secondary text-secondary px-6 py-3 rounded-full font-bold hover:bg-secondary hover:text-white transition-all"
        >
          Open Document
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="bg-secondary text-white px-6 py-3 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all"
        >
          Download
        </button>
      </div>
    </div>
  );

  const renderPreviewContent = () => {
    if (!doc.content) {
      return (
        <div className="h-full flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 warm-tactile-shadow">
            <span className="material-symbols-outlined text-3xl text-secondary">lock</span>
          </div>
          <p className="text-base font-bold text-primary-dark mb-2">Document not available</p>
          <p className="text-sm font-medium text-slate-500">Contact your board administrator for access.</p>
        </div>
      );
    }

    if (previewKind === 'image') {
      return (
        <div className="min-h-full flex items-start justify-center">
          <img
            src={doc.content}
            alt={doc.title}
            className="max-w-none rounded-xl border border-amber-100/40 bg-white"
            style={{
              width: `${Math.round(Math.min(Math.max(availableWidth - 64, 360), 1200) * scale)}px`,
              height: 'auto',
            }}
          />
        </div>
      );
    }

    if (previewKind === 'word') {
      if (previewLoading) {
        return (
          <div className="h-full flex flex-col items-center justify-center text-center py-24">
            <span className="material-symbols-outlined text-4xl text-secondary/60 animate-spin mb-4">progress_activity</span>
            <p className="text-sm font-bold text-primary-dark">Preparing Word preview...</p>
          </div>
        );
      }
      if (viewerError) return renderUnsupportedState(viewerError);
      return (
        <article
          className="mx-auto max-w-4xl bg-white rounded-2xl border border-amber-100/40 px-8 sm:px-12 py-10 text-primary-dark leading-relaxed
            [&_p]:mb-4 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-5 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-4
            [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2 [&_table]:w-full [&_table]:border-collapse
            [&_td]:border [&_td]:border-slate-200 [&_td]:p-2 [&_th]:border [&_th]:border-slate-200 [&_th]:p-2"
          style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}
          dangerouslySetInnerHTML={{ __html: wordHtml }}
        />
      );
    }

    if (previewKind === 'spreadsheet') {
      if (previewLoading) {
        return (
          <div className="h-full flex flex-col items-center justify-center text-center py-24">
            <span className="material-symbols-outlined text-4xl text-secondary/60 animate-spin mb-4">progress_activity</span>
            <p className="text-sm font-bold text-primary-dark">Preparing spreadsheet preview...</p>
          </div>
        );
      }
      if (viewerError) return renderUnsupportedState(viewerError);
      return (
        <div className="space-y-8 min-w-max">
          {spreadsheetSheets.map((sheet) => (
            <section key={sheet.name} className="bg-white rounded-2xl border border-amber-100/40 overflow-hidden">
              <div className="px-5 py-3 border-b border-amber-100/60 bg-section-bg">
                <h3 className="text-sm font-bold text-primary-dark">{sheet.name}</h3>
              </div>
              <div className="overflow-auto">
                <table className="text-sm border-collapse">
                  <tbody>
                    {sheet.rows.map((row, rowIndex) => (
                      <tr key={`${sheet.name}-${rowIndex}`} className={rowIndex === 0 ? 'bg-amber-50/70' : ''}>
                        {(row.length ? row : ['']).map((cell, cellIndex) => (
                          <td
                            key={`${sheet.name}-${rowIndex}-${cellIndex}`}
                            className="border border-slate-200 px-3 py-2 text-primary-dark whitespace-pre-wrap min-w-[8rem]"
                          >
                            {String(cell ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      );
    }

    if (previewKind === 'text') {
      if (previewLoading) {
        return (
          <div className="h-full flex flex-col items-center justify-center text-center py-24">
            <span className="material-symbols-outlined text-4xl text-secondary/60 animate-spin mb-4">progress_activity</span>
            <p className="text-sm font-bold text-primary-dark">Loading text preview...</p>
          </div>
        );
      }
      if (viewerError) return renderUnsupportedState(viewerError);
      return (
        <pre className="mx-auto max-w-5xl bg-white rounded-2xl border border-amber-100/40 p-8 text-sm leading-relaxed text-primary-dark whitespace-pre-wrap">
          {textPreview}
        </pre>
      );
    }

    if (previewKind === 'browser') {
      return (
        <iframe
          src={doc.content}
          title={doc.title}
          className="w-full h-full min-h-[65vh] rounded-2xl bg-white border border-amber-100/40"
        />
      );
    }

    return renderUnsupportedState();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-primary-dark/70 backdrop-blur-sm p-4 sm:p-8 lg:p-10"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-[1600px] mx-auto bg-main-bg rounded-[2rem] overflow-hidden warm-tactile-shadow border border-amber-100/30 flex flex-col"
        onClick={(event) => event.stopPropagation()}
        tabIndex={-1}
        ref={(element) => element && element.focus()}
      >
        <header className="bg-white border-b border-amber-100/60 px-4 sm:px-6 py-3 flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-section-bg flex items-center justify-center hover:bg-amber-100/70 transition-colors flex-shrink-0"
            aria-label="Close document viewer"
          >
            <span className="material-symbols-outlined text-xl text-primary-dark">close</span>
          </button>

          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Document Viewer</p>
            <h2 className="text-sm sm:text-base font-bold text-primary-dark truncate">{doc.title}</h2>
          </div>

          <span className="hidden sm:inline-flex text-xs font-bold px-3 py-1.5 rounded-full bg-secondary/10 text-secondary border border-secondary/20 flex-shrink-0">
            {categoryLabel(doc.category)}
          </span>

          <div className="hidden md:flex items-center gap-2 px-2 py-1.5 rounded-full bg-section-bg border border-amber-100/50 flex-shrink-0">
            <button
              type="button"
              onClick={() => setScale((value) => Math.max(value - 0.1, 0.7))}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white transition-colors"
              aria-label="Zoom out"
            >
              <span className="material-symbols-outlined text-lg text-primary-dark">remove</span>
            </button>
            <span className="text-xs font-bold text-primary-dark w-12 text-center">{Math.round(scale * 100)}%</span>
            <button
              type="button"
              onClick={() => setScale((value) => Math.min(value + 0.1, 1.8))}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white transition-colors"
              aria-label="Zoom in"
            >
              <span className="material-symbols-outlined text-lg text-primary-dark">add</span>
            </button>
          </div>

          <button
            type="button"
            onClick={openInNewTab}
            className="hidden sm:flex w-10 h-10 rounded-full bg-section-bg items-center justify-center hover:bg-amber-100/70 transition-colors"
            aria-label="Open document in new tab"
          >
            <span className="material-symbols-outlined text-xl text-primary-dark">open_in_new</span>
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="w-10 h-10 rounded-full bg-secondary text-white flex items-center justify-center hover:brightness-110 active:scale-95 transition-all"
            aria-label="Download document"
          >
            <span className="material-symbols-outlined text-xl">download</span>
          </button>
        </header>

        <div className="flex-1 min-h-0 flex bg-section-bg">
          {isPdfPreview && sidebarOpen && numPages && (
            <aside className="hidden lg:flex w-[clamp(8rem,13vw,13rem)] flex-shrink-0 border-r border-amber-100/60 bg-[#1D2B20] text-white flex-col">
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Pages</p>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors"
                  aria-label="Hide page thumbnails"
                >
                  <span className="material-symbols-outlined text-lg">left_panel_close</span>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <Document file={doc.content} loading={null}>
                  {Array.from(new Array(numPages), (_, index) => {
                    const pageNumber = index + 1;
                    const isActive = pageNumber === currentPage;
                    return (
                      <button
                        type="button"
                        key={`thumb_${pageNumber}`}
                        onClick={() => scrollToPage(pageNumber)}
                        className="w-full text-left group"
                      >
                        <div className={`bg-white rounded-xl overflow-hidden border-2 transition-all flex justify-center ${
                          isActive ? 'border-secondary' : 'border-white/10 group-hover:border-secondary/60'
                        }`}>
                          <Page
                            pageNumber={pageNumber}
                            width={118}
                            renderAnnotationLayer={false}
                            renderTextLayer={false}
                            loading={<div className="w-[118px] h-[150px] bg-section-bg" />}
                          />
                        </div>
                        <p className={`text-center text-xs font-bold mt-2 ${isActive ? 'text-secondary' : 'text-white/60'}`}>
                          {pageNumber}
                        </p>
                      </button>
                    );
                  })}
                </Document>
              </div>
            </aside>
          )}

          <main className="flex-1 min-w-0 flex flex-col">
            <div className="h-12 px-4 sm:px-6 border-b border-amber-100/60 bg-white/80 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen((value) => !value)}
                  className={`${isPdfPreview ? 'hidden lg:flex' : 'hidden'} w-8 h-8 rounded-full bg-section-bg items-center justify-center hover:bg-amber-100/70 transition-colors`}
                  aria-label={sidebarOpen ? 'Hide page thumbnails' : 'Show page thumbnails'}
                >
                  <span className="material-symbols-outlined text-lg text-primary-dark">
                    {sidebarOpen ? 'left_panel_close' : 'left_panel_open'}
                  </span>
                </button>
                <p className="text-xs font-bold text-slate-500 truncate">
                  {previewLabel(doc)}{formatSize(doc.file_size_bytes) ? ` | ${formatSize(doc.file_size_bytes)}` : ''}{isPdfPreview && numPages ? ` | ${numPages} pages` : ''}
                </p>
              </div>

              {isPdfPreview && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={showPreviousPage}
                  disabled={currentPage <= 1}
                  className="w-8 h-8 rounded-full bg-section-bg flex items-center justify-center hover:bg-amber-100/70 transition-colors disabled:opacity-40"
                  aria-label="Previous page"
                >
                  <span className="material-symbols-outlined text-lg text-primary-dark">keyboard_arrow_up</span>
                </button>
                <span className="text-xs font-bold text-primary-dark min-w-[4.5rem] text-center">
                  {currentPage}{numPages ? ` / ${numPages}` : ''}
                </span>
                <button
                  type="button"
                  onClick={showNextPage}
                  disabled={!numPages || currentPage >= numPages}
                  className="w-8 h-8 rounded-full bg-section-bg flex items-center justify-center hover:bg-amber-100/70 transition-colors disabled:opacity-40"
                  aria-label="Next page"
                >
                  <span className="material-symbols-outlined text-lg text-primary-dark">keyboard_arrow_down</span>
                </button>
              </div>
              )}
            </div>

            <div ref={viewportRef} className="flex-1 min-h-0 overflow-auto bg-main-bg px-4 sm:px-6 py-6">
              {isPdfPreview && doc.content ? (
                <Document
                  file={doc.content}
                  onLoadSuccess={({ numPages: loadedPages }) => {
                    setNumPages(loadedPages);
                    setViewerError('');
                  }}
                  onLoadError={() => setViewerError('This document could not be loaded in the portal viewer.')}
                  loading={
                    <div className="h-full flex flex-col items-center justify-center text-center py-24">
                      <span className="material-symbols-outlined text-4xl text-secondary/60 animate-spin mb-4">progress_activity</span>
                      <p className="text-sm font-bold text-primary-dark">Loading document...</p>
                    </div>
                  }
                  error={
                    <div className="h-full flex flex-col items-center justify-center text-center py-24">
                      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 warm-tactile-shadow">
                        <span className="material-symbols-outlined text-3xl text-secondary">error</span>
                      </div>
                      <p className="text-base font-bold text-primary-dark mb-2">Preview unavailable</p>
                      <p className="text-sm font-medium text-slate-500 mb-6">{viewerError || 'Open or download the document to view it.'}</p>
                      <button
                        type="button"
                        onClick={openInNewTab}
                        className="bg-secondary text-white px-6 py-3 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all"
                      >
                        Open Document
                      </button>
                    </div>
                  }
                  className="flex flex-col items-center min-w-max"
                >
                  {numPages && Array.from(new Array(numPages), (_, index) => {
                    const pageNumber = index + 1;
                    return (
                      <div
                        key={`page_${pageNumber}`}
                        data-page-number={pageNumber}
                        ref={(element) => { pageRefs.current[pageNumber] = element; }}
                        className="mb-6 bg-white rounded-xl overflow-hidden border border-amber-100/40"
                      >
                        <Page
                          pageNumber={pageNumber}
                          width={pageWidth}
                          renderAnnotationLayer
                          renderTextLayer
                          loading={<div className="h-[70vh] w-full min-w-[360px] bg-white flex items-center justify-center text-sm font-bold text-slate-400">Loading page {pageNumber}...</div>}
                        />
                      </div>
                    );
                  })}
                </Document>
              ) : (
                renderPreviewContent()
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default function MemberLibrary() {
  const { user, isLoading: authLoading } = useAuth();
  const { hoa, isLoading: hoaLoading } = useHOA();

  // Documents
  const [documents, setDocuments] = useState([]);
  const [libraryFolders, setLibraryFolders] = useState(FOLDERS);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Finder navigation
  const [activeFolder, setActiveFolder] = useState(null);
  const [sliding, setSliding] = useState(false);

  // PDF viewer
  const [openDoc, setOpenDoc] = useState(null);
  const [openingDocId, setOpeningDocId] = useState(null);
  const signedUrlCacheRef = useRef(new Map());
  const linkPrewarmRunRef = useRef(0);

  // AI Search — conversation[] is single source of truth
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  // Thumbs feedback per answer, keyed by rag_query_log id: true | false
  const [answerRatings, setAnswerRatings] = useState({});
  const [followUpQuery, setFollowUpQuery] = useState('');
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [aiInputFocused, setAiInputFocused] = useState(false);
  const aiInputRef = useRef(null);
  const threadIdRef = useRef(null);

  const hydrateDocumentLinks = useCallback(async (docs) => {
    const docsNeedingLinks = (docs || []).filter(doc => (
      doc.has_file &&
      !doc.content &&
      !signedUrlCacheRef.current.has(doc.id)
    ));

    if (docsNeedingLinks.length === 0) return;

    const runId = linkPrewarmRunRef.current + 1;
    linkPrewarmRunRef.current = runId;
    const signedUrls = await documentStorage.getSignedUrlsForDocuments(docsNeedingLinks, 3600);

    if (runId !== linkPrewarmRunRef.current || signedUrls.size === 0) return;

    signedUrls.forEach((url, documentId) => {
      if (url) signedUrlCacheRef.current.set(documentId, url);
    });

    setDocuments(currentDocs => currentDocs.map(doc => {
      const signedUrl = signedUrlCacheRef.current.get(doc.id);
      return signedUrl && doc.content !== signedUrl ? { ...doc, content: signedUrl } : doc;
    }));
  }, []);

  const loadDocuments = useCallback(async () => {
    if (authLoading || hoaLoading) {
      setIsLoading(true);
      return;
    }

    if (!user?.id || !hoa?.id) {
      setIsLoading(false);
      setLoadError('Portal session is not ready. Please refresh or sign in again.');
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const [docs, folders] = await Promise.all([
        documentStorage.getDocumentLibraryItems({
          throwOnError: true,
          hoaId: hoa.id,
          visibleToHomeownersOnly: (user.portalRole || 'member') === 'member',
        }),
        documentStorage.getDocumentFolders({ hoaId: hoa.id }),
      ]);
      const visibleFolders = (folders || [])
        .filter(folder => folder.system_key !== 'all')
        .map(folderFromDb);
      const sortedDocs = sortLibraryDocuments(docs || []);
      const visibleDocumentIds = new Set(sortedDocs.map(doc => doc.id));
      signedUrlCacheRef.current.forEach((_, documentId) => {
        if (!visibleDocumentIds.has(documentId)) signedUrlCacheRef.current.delete(documentId);
      });
      setDocuments(sortedDocs);
      hydrateDocumentLinks(sortedDocs).then(null, error => {
        console.warn('Document link prewarm failed:', error?.message || error);
      });
      setLibraryFolders(visibleFolders.length ? visibleFolders : FOLDERS);
    } catch (e) {
      console.error('MemberLibrary load error:', e);
      setDocuments([]);
      setLoadError(e?.message || 'Documents could not be loaded.');
      logEvent('documents_fetch_error', {
        portal_role: user.portalRole || 'member',
        source: 'MemberLibrary',
        code: e?.code || null,
        message: e?.message || 'Documents could not be loaded.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [authLoading, hoaLoading, hoa?.id, hydrateDocumentLinks, user?.id, user?.portalRole]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    const reloadOnWake = () => {
      if (document.visibilityState === 'visible') loadDocuments();
    };
    const reloadOnFocus = () => loadDocuments();

    document.addEventListener('visibilitychange', reloadOnWake);
    window.addEventListener('focus', reloadOnFocus);
    window.addEventListener('online', reloadOnFocus);

    return () => {
      document.removeEventListener('visibilitychange', reloadOnWake);
      window.removeEventListener('focus', reloadOnFocus);
      window.removeEventListener('online', reloadOnFocus);
    };
  }, [loadDocuments]);
  // Browser back button closes modal
  useEffect(() => {
    if (!openDoc) return;
    window.history.pushState({ docOpen: true }, '');
    const handlePop = () => setOpenDoc(null);
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [openDoc]);

  // Always-on Escape key listener — no dep array, fires every render
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && openDoc) {
        setOpenDoc(null);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  });

  const docsForFolder = (folder) => {
    if (!folder) return documents;
    if (folder.id) return sortLibraryDocuments(documents.filter(d => d.folder_id === folder.id));
    return sortLibraryDocuments(documents.filter(d => !d.folder_id && folder.categories.includes(d.category)));
  };

  const navigateToFolder = (folder) => {
    setSliding(true);
    setTimeout(() => {
      setActiveFolder(folder);
      setSliding(false);
    }, 280);
  };

  const navigateBack = () => {
    setSliding(true);
    setTimeout(() => {
      setActiveFolder(null);
      setSliding(false);
    }, 280);
  };

  // FIX 1A: handleAiSearch — conversation[] is single source of truth
  const runAiQuery = async (q) => {
    if (!q || aiLoading) return;
    setAiInputFocused(false);
    setAiLoading(true);
    setConversation([]);
    try {
      threadIdRef.current = createId();
      const result = await runRAGQuery(supabase, hoa.id, user.id, user.portalRole, q, [], threadIdRef.current);
      setConversation([{
        query: q,
        result: {
          logId: result.logId || null,
          answer: result.answer,
          citations: result.citations || [],
          confidence: result.confidence,
        },
      }]);
      setAiQuery('');
    } catch (e) {
      setConversation([{
        query: q,
        result: {
          answer: 'Something went wrong. Please try again.',
          citations: [],
        },
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiSearch = () => runAiQuery(aiQuery.trim());

  const handleRateAnswer = (turn, rating) => {
    const logId = turn?.result?.logId;
    if (!logId || answerRatings[logId] !== undefined) return;
    setAnswerRatings(prev => ({ ...prev, [logId]: rating }));
    rateAnswer({
      logId,
      rating,
      query: turn.query,
      answerLength: (turn.result.answer || '').length,
    }).then(null, error => {
      console.warn('AI answer rating could not be saved:', error?.message || error);
    });
  };

  const handleExampleQueryClick = (text) => {
    setAiQuery(text);
    runAiQuery(text);
  };

  // FIX 1B: handleFollowUp — appends to conversation[], no aiResult swap
  const handleFollowUp = async () => {
    const q = followUpQuery.trim();
    if (!q || followUpLoading) return;
    setFollowUpLoading(true);
    try {
      const historyMessages = conversation.flatMap(turn => [
        { role: 'user', content: turn.query },
        { role: 'assistant', content: turn.result.answer },
      ]);

      const result = await runRAGQuery(
        supabase,
        hoa.id,
        user.id,
        user.portalRole,
        q,
        historyMessages,
        threadIdRef.current
      );

      setConversation(prev => [...prev, {
        query: q,
        result: {
          logId: result.logId || null,
          answer: result.answer,
          citations: result.citations || [],
        },
      }]);
      setFollowUpQuery('');
    } catch (e) {
      console.error('Follow-up error:', e);
    } finally {
      setFollowUpLoading(false);
    }
  };

  const handleAiKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAiSearch();
    }
  };

  // FIX 1D: clear resets conversation
  const clearAiResult = () => {
    setConversation([]);
    setAiQuery('');
    setFollowUpQuery('');
    aiInputRef.current?.focus();
  };

  // ── OPEN DOCUMENT BY ID — shared by DocRow and AI Search source citations ──
  const openDocumentById = useCallback(async (docId) => {
    if (openingDocId === docId) return;
    const doc = documents.find(d => d.id === docId);
    if (!doc) return;

    const useNativeOpen = shouldUseNativeDocumentOpen();
    const cachedSignedUrl = signedUrlCacheRef.current.get(doc.id) || doc.content || null;

    if (!doc.has_file) {
      if (useNativeOpen) {
        const reservedWindow = reserveNativeDocumentWindow();
        showReservedDocumentWindowError(reservedWindow, 'This document does not have a downloadable file yet.');
        return;
      }
      setOpenDoc(doc);
      return;
    }

    if (useNativeOpen && cachedSignedUrl) {
      openDocumentUrlNatively(cachedSignedUrl, doc.file_name || doc.title || 'document');
      logEvent('document_view', {
        document_id: doc.id,
        title: doc.title,
        category: doc.category,
        open_mode: 'native_mobile',
      });
      return;
    }

    const reservedWindow = useNativeOpen ? reserveNativeDocumentWindow() : null;
    setOpeningDocId(doc.id);
    try {
      const signedUrl = cachedSignedUrl || await documentStorage.createSignedUrlForPath(doc.file_url, 3600);
      if (!signedUrl) throw new Error('The document link could not be prepared.');

      signedUrlCacheRef.current.set(doc.id, signedUrl);
      const docWithFile = { ...doc, content: signedUrl };
      setDocuments(currentDocs => currentDocs.map(item => (
        item.id === doc.id ? { ...item, content: signedUrl } : item
      )));

      if (useNativeOpen) {
        openDocumentUrlNatively(signedUrl, doc.file_name || doc.title || 'document', reservedWindow);
        logEvent('document_view', {
          document_id: doc.id,
          title: doc.title,
          category: doc.category,
          open_mode: 'native_mobile',
        });
        return;
      }

      setOpenDoc(docWithFile);
      logEvent('document_view', {
        document_id: doc.id,
        title: doc.title,
        category: doc.category,
        open_mode: 'portal_preview',
      });
    } catch (e) {
      console.error('Document open error:', e);
      if (useNativeOpen) {
        showReservedDocumentWindowError(reservedWindow, e?.message || 'The document link could not be prepared.');
        return;
      }
      setOpenDoc({ ...doc, content: null });
    } finally {
      setOpeningDocId(null);
    }
  }, [documents, openingDocId]);

  // ── DOCUMENT ROW ──
  const DocRow = ({ doc }) => {
    const handleDocClick = () => openDocumentById(doc.id);

    return (
    <div
      className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-slate-200/40
        cursor-pointer hover:border-secondary/40 transition-all warm-tactile-shadow min-h-[48px]"
      onClick={handleDocClick}
    >
      <div className="w-11 h-11 bg-section-bg rounded-xl flex items-center justify-center flex-shrink-0">
        <span className="material-symbols-outlined text-xl text-secondary">{documentIcon(doc)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-primary-dark truncate leading-snug">
          {doc.title}
        </p>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary/10 text-secondary">
            {categoryLabel(doc.category)}
          </span>
          {doc.phase_applicability && doc.phase_applicability !== 'both' && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
              {doc.phase_applicability === 'phase1' ? 'Phase 1' : 'Phase 2'}
            </span>
          )}
          <span className="text-[10px] text-slate-400 font-medium hidden md:inline">{formatDate(doc.created_at)}</span>
          {doc.file_size_bytes && (
            <span className="text-[10px] text-slate-400 font-medium hidden md:inline">{formatSize(doc.file_size_bytes)}</span>
          )}
        </div>
      </div>
      {openingDocId === doc.id ? (
        <span className="material-symbols-outlined text-secondary animate-spin flex-shrink-0">progress_activity</span>
      ) : (
        <span className="material-symbols-outlined text-slate-300 flex-shrink-0">chevron_right</span>
      )}
    </div>
    );
  };

  // ── MAIN RENDER ──
  return (
    <div className="space-y-6">

      {/* ── AI SEARCH BAR ── */}
      <section className="bg-[#E7DDC4] rounded-[2rem] p-4 sm:p-7">

        {/* Search input row */}
        <div className="flex items-center gap-3 bg-white rounded-2xl px-4 sm:px-5 py-4
          warm-tactile-shadow border border-amber-200/40">
          <span className="material-symbols-outlined text-2xl text-secondary flex-shrink-0">
            auto_awesome
          </span>
          <input
            ref={aiInputRef}
            type="text"
            value={aiQuery}
            onChange={e => setAiQuery(e.target.value)}
            onKeyDown={handleAiKeyDown}
            onFocus={() => setAiInputFocused(true)}
            onBlur={() => setTimeout(() => setAiInputFocused(false), 150)}
            placeholder="Not just a search bar. Ask a real question, get a real answer."
            className="flex-1 min-w-0 text-base md:text-sm font-medium text-primary-dark placeholder-slate-400
              bg-transparent outline-none"
          />
          {(aiQuery || conversation.length > 0) && (
            <button
              onClick={clearAiResult}
            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center
                hover:bg-slate-200 transition-colors flex-shrink-0"
            >
              <span className="material-symbols-outlined text-sm text-slate-400">close</span>
            </button>
          )}
          <button
            onClick={handleAiSearch}
            disabled={!aiQuery.trim() || aiLoading}
            className="touch-target rounded-full bg-secondary flex items-center justify-center
              hover:brightness-110 active:scale-95 transition-all flex-shrink-0
              disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {aiLoading
              ? <span className="material-symbols-outlined text-white text-lg animate-spin">progress_activity</span>
              : <span className="material-symbols-outlined text-white text-lg">send</span>
            }
          </button>
        </div>

        {/* Example query chips — shown on focus before a search has run */}
        {aiInputFocused && conversation.length === 0 && !aiLoading && (
          <div className="mt-4 animate-slide-up">
            <p className="text-[10px] font-bold uppercase tracking-widest text-primary-dark/40 mb-2.5 px-1">
              Try asking
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {EXAMPLE_QUERIES.map((example, i) => (
                <button
                  key={i}
                  type="button"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => handleExampleQueryClick(example.text)}
                  className="flex items-center gap-3 text-left bg-white/70 hover:bg-white
                    rounded-2xl px-4 py-3.5 border border-amber-200/40 transition-all
                    hover:border-secondary/40 hover:-translate-y-0.5"
                >
                  <span className="material-symbols-outlined text-lg text-secondary flex-shrink-0">
                    {example.icon}
                  </span>
                  <span className="text-sm font-medium text-primary-dark leading-snug">
                    {example.text}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sub-signature */}
        {!aiInputFocused && conversation.length === 0 && !aiLoading && (
          <p className="text-[11px] font-bold uppercase tracking-widest text-secondary/70 mt-3 text-center">
            Powered by Claude AI
          </p>
        )}

        {/* Loading state */}
        {aiLoading && (
          <div className="mt-5 flex items-center gap-3">
            <div className="w-8 h-8 bg-white/60 rounded-xl flex items-center justify-center">
              <span className="material-symbols-outlined text-lg text-secondary animate-spin">
                progress_activity
              </span>
            </div>
            <p className="text-sm font-medium text-primary-dark/60">
              Searching your governing documents...
            </p>
          </div>
        )}

        {/* FIX 1C: Full conversation thread */}
        {conversation.length > 0 && (
          <div className="mt-5 animate-slide-up">

            <div className="space-y-6">
              {conversation.map((turn, i) => (
                <div key={i}>

                  {/* User question bubble */}
                  <div className="flex justify-end mb-3">
                    <div className="bg-white rounded-2xl rounded-tr-sm px-5 py-3
                      warm-tactile-shadow max-w-[85%]">
                      <p className="text-sm font-medium text-primary-dark">
                        {turn.query}
                      </p>
                    </div>
                  </div>

                  {/* AI answer bubble */}
                  <div className="bg-white/70 rounded-2xl rounded-tl-sm px-6 py-5
                    border border-amber-200/30">
                    <div className="space-y-1">
                      {renderAnswer(turn.result.answer || '')}
                    </div>
                    {turn.result.confidence === 'low' && (
                      <div className="mt-3 flex items-center gap-2 text-xs font-semibold
                        text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                        <span className="material-symbols-outlined text-sm flex-shrink-0">info</span>
                        Couldn't find a document to confirm this — please check with the board or property manager.
                      </div>
                    )}
                    {turn.result.citations && turn.result.citations.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-amber-900/10">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                          Sources
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {turn.result.citations.map((c, ci) => (
                            <button
                              key={ci}
                              type="button"
                              onClick={() => c.document_id && openDocumentById(c.document_id)}
                              disabled={!c.document_id || openingDocId === c.document_id}
                              className="flex items-center gap-1.5 text-xs font-semibold text-secondary
                                bg-secondary/10 hover:bg-secondary/20 rounded-full pl-3 pr-3 py-1.5
                                max-w-[220px] transition-colors disabled:opacity-60 disabled:cursor-default"
                              title={c.section_header || c.document_name}
                            >
                              {openingDocId === c.document_id
                                ? <span className="material-symbols-outlined text-sm flex-shrink-0 animate-spin">progress_activity</span>
                                : <span className="material-symbols-outlined text-sm flex-shrink-0">description</span>
                              }
                              <span className="truncate">{c.document_name}</span>
                              {c.document_id && (
                                <span className="material-symbols-outlined text-xs flex-shrink-0">open_in_new</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {turn.result.logId && (
                      <div className="mt-4 pt-3 border-t border-amber-900/10 flex flex-wrap items-center justify-end gap-2">
                        {answerRatings[turn.result.logId] === undefined ? (
                          <>
                            <span className="text-xs font-medium text-slate-500">Was this helpful?</span>
                            <button
                              type="button"
                              onClick={() => handleRateAnswer(turn, true)}
                              aria-label="Helpful"
                              title="Helpful"
                              className="w-9 h-9 rounded-full bg-white border border-amber-200/50 text-slate-500
                                hover:text-secondary hover:border-secondary/40 flex items-center justify-center transition-colors"
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>thumb_up</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRateAnswer(turn, false)}
                              aria-label="Not helpful"
                              title="Not helpful"
                              className="w-9 h-9 rounded-full bg-white border border-amber-200/50 text-slate-500
                                hover:text-secondary hover:border-secondary/40 flex items-center justify-center transition-colors"
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>thumb_down</span>
                            </button>
                          </>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-secondary">
                            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                              {answerRatings[turn.result.logId] ? 'thumb_up' : 'thumb_down'}
                            </span>
                            Thanks for the feedback
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                </div>
              ))}
            </div>

            {/* Follow-up input */}
            <div className="mt-5 sticky bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-10 md:static">
              <p className="text-[11px] font-bold uppercase tracking-widest text-secondary/70 mb-2 px-1">
                Have another question? Just ask.
              </p>
              <div className="flex items-center gap-3 bg-white rounded-2xl px-5 py-3.5
                warm-tactile-shadow border border-amber-200/30">
                <input
                  type="text"
                  value={followUpQuery}
                  onChange={e => setFollowUpQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleFollowUp();
                    }
                  }}
                  placeholder='e.g. "What about Phase 2?"'
                  className="flex-1 min-w-0 text-base md:text-sm font-medium text-primary-dark
                    placeholder-slate-400 bg-transparent outline-none"
                />
                <button
                  onClick={handleFollowUp}
                  disabled={!followUpQuery.trim() || followUpLoading}
                  className="touch-target rounded-full bg-secondary flex items-center
                    justify-center hover:brightness-110 active:scale-95 transition-all
                    disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                >
                  {followUpLoading
                    ? <span className="material-symbols-outlined text-white text-sm animate-spin">progress_activity</span>
                    : <span className="material-symbols-outlined text-white text-sm">send</span>
                  }
                </button>
              </div>
              <button
                onClick={clearAiResult}
                className="mt-3 flex items-center gap-1.5 text-xs font-medium
                  text-slate-400 hover:text-secondary transition-colors"
              >
                <span className="material-symbols-outlined text-sm">refresh</span>
                Start new search
              </button>
            </div>

          </div>
        )}
      </section>

      {/* ── FINDER-STYLE NAVIGATOR ── */}
      <section className="bg-section-bg rounded-[2rem] overflow-hidden">

        <div className="flex flex-col md:flex-row" style={{ minHeight: '480px' }}>

          {/* LEFT COLUMN — Folder list */}
          <div className="hidden md:flex w-56 flex-shrink-0 border-r border-white/60 flex-col">
            <div className="px-5 py-4 border-b border-white/60">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Library
              </p>
            </div>

            {/* All Documents row */}
            <button
              onClick={() => activeFolder && navigateBack()}
              className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-all
                ${!activeFolder
                  ? 'bg-white text-primary-dark font-bold'
                  : 'text-slate-500 hover:bg-white/50 font-medium'
                }`}
            >
              <span className={`material-symbols-outlined text-[18px]
                ${!activeFolder ? 'text-secondary' : 'text-slate-400'}`}>
                folder_open
              </span>
              <span className="text-sm flex-1">All Documents</span>
              <span className="text-[10px] font-bold text-slate-400">
                {documents.length}
              </span>
            </button>

            {/* Folder rows */}
            {libraryFolders.map(folder => {
              const count = docsForFolder(folder).length;
              const isActive = activeFolder?.key === folder.key;
              return (
                <button
                  key={folder.key}
                  onClick={() => navigateToFolder(folder)}
                  className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-all
                    ${isActive
                      ? 'bg-white text-primary-dark font-bold'
                      : 'text-slate-500 hover:bg-white/50 font-medium'
                    }`}
                >
                  <span className={`material-symbols-outlined text-[18px]
                    ${isActive ? 'text-secondary' : 'text-slate-400'}`}>
                    {folder.icon}
                  </span>
                  <span className="text-sm flex-1 truncate">{folder.label}</span>
                  {count > 0 && (
                    <span className="text-[10px] font-bold text-slate-400">{count}</span>
                  )}
                  {isActive && (
                    <span className="material-symbols-outlined text-[14px] text-secondary">
                      chevron_right
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Mobile: horizontal folder pills */}
          <div className="flex md:hidden gap-2 px-5 pt-5 pb-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => activeFolder && navigateBack()}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                !activeFolder
                  ? 'bg-white text-primary-dark border border-secondary/30'
                  : 'bg-white/50 text-slate-500 border border-transparent'
              }`}
            >
              <span className="material-symbols-outlined text-sm">folder_open</span>
              All
            </button>
            {libraryFolders.map(folder => {
              const isActive = activeFolder?.key === folder.key;
              return (
                <button
                  key={folder.key}
                  onClick={() => navigateToFolder(folder)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-white text-primary-dark border border-secondary/30'
                      : 'bg-white/50 text-slate-500 border border-transparent'
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">{folder.icon}</span>
                  {folder.label.replace(' Reports', '').replace(' Documents', '')}
                </button>
              );
            })}
          </div>

          {/* RIGHT PANEL — Document list with slide animation */}
          <div
            className="flex-1 p-5 space-y-3"
            style={{
              opacity: sliding ? 0 : 1,
              transition: 'opacity 0.22s ease',
            }}
          >
            {/* Panel header */}
            <div className="flex items-center justify-between pb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                {activeFolder ? activeFolder.label : 'All Documents'}
              </p>
              <span className="text-[10px] font-bold text-slate-400">
                {(activeFolder ? docsForFolder(activeFolder) : documents).length} docs
              </span>
            </div>

            {/* Loading */}
            {(authLoading || hoaLoading || isLoading) && (
              <div className="flex items-center justify-center py-20">
                <span className="material-symbols-outlined text-3xl text-secondary/40 animate-spin">
                  progress_activity
                </span>
              </div>
            )}

            {/* Error state */}
            {!authLoading && !hoaLoading && !isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-4 warm-tactile-shadow">
                  <span className="material-symbols-outlined text-2xl text-secondary/70">error</span>
                </div>
                <p className="text-sm font-bold text-primary-dark mb-1">Could not load documents</p>
                <p className="text-xs font-medium text-slate-400 max-w-[260px] leading-relaxed mb-5">
                  {loadError}
                </p>
                <button
                  onClick={loadDocuments}
                  className="bg-secondary text-white px-6 py-2.5 rounded-full font-bold text-xs hover:brightness-110 active:scale-95 transition-all"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Empty state */}
            {!authLoading && !hoaLoading && !isLoading && !loadError && (activeFolder ? docsForFolder(activeFolder) : documents).length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-4 warm-tactile-shadow">
                  <span className="material-symbols-outlined text-2xl text-secondary/50">folder_open</span>
                </div>
                <p className="text-sm font-bold text-primary-dark mb-1">No documents yet</p>
                <p className="text-xs font-medium text-slate-400 max-w-[200px] leading-relaxed">
                  {activeFolder
                    ? `${activeFolder.label} will appear here once uploaded.`
                    : 'Documents will appear here once uploaded by your board.'}
                </p>
              </div>
            )}

            {/* Document rows */}
            {!authLoading && !hoaLoading && !isLoading && !loadError && (activeFolder ? docsForFolder(activeFolder) : documents).map((doc, i) => (
              <DocRow key={doc.id} doc={doc} />
            ))}

          </div>
        </div>
      </section>

      {/* Document Preview Modal */}
      {openDoc && (
        <MemberDocumentViewer doc={openDoc} onClose={() => setOpenDoc(null)} />
      )}

    </div>
  );
}
